import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { type UserFindInterface, UserFindDto } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { type DealStatusFindInterface, DealStatusFindDto } from '@zaoblako/shared/modules/business/dto/deal-status/deal-status-find.dto';
import { type ContragentFindInterface, ContragentFindDto } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { type CompanyFindInterface, CompanyFindDto } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { type DirectionFindInterface, DirectionFindDto } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { type UnitFindInterface, UnitFindDto } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { type FilialFindInterface, FilialFindDto } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import { type DealsGroupFindInterface, DealsGroupFindDto } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import { type UserGroupFindInterface, UserGroupFindDto } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { type DealSynchronizeHistoryInterface, DealSynchronizeHistoryDto } from '@zaoblako/shared/modules/business/dto/deal/deal-synchronize-history-find.dto';
import { type DocumentsStatisticsFindInterface, DocumentsStatisticsFindDto } from '@zaoblako/shared/modules/business/dto/documents-statistics-find.dto';
import { type ReminderFindInterface, ReminderFindDto } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import { type DealOrderFindInterface, DealOrderFindDto } from '@zaoblako/shared/modules/business/dto/deal-order/deal-order-find.dto';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import { ProjectDtoKeysEnum } from '@zaoblako/shared/modules/project/enum/project-dto-keys.enum';
import { HelpdeskDtoKeyEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk-dto-key.enum';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { ThreadFindInterface } from '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';

import * as yup from 'yup';

export interface DealFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Deal creation date and time */
  created: Date;
  /** Deal last update date and time */
  updated: Date;
  /** Deal shipping date and time */
  shippingDate: Date;
  /** Is deal deleted */
  deleted: Date;
  /** Deal display number */
  number: string;
  /** Deal display name */
  name: string;
  /** Deal amount in roubles */
  amount: number;
  /** Deal commentary (just some text, maybe formatted) */
  comment?: string;
  /** Accrual method documents: income and expense difference */
  saleDiff?: number;
  /** Number of all files attached to deal */
  filesCount?: number;
  priority?: HelpdeskTaskPriorityEnum;
  /** Planned time to spend for deal in minutes (`0` by default) */
  plannedMin?: number;
  factMin: number;
  amountCharges?: number;
  amountPayment?: number;
  /** Deal creator */
  createdBy?: UserFindInterface;
  /** Deal responsible */
  responsible?: UserFindInterface;
  /** Current deal status */
  status: DealStatusFindInterface;
  /** Deal's counterparty */
  contragent?: ContragentFindInterface;
  /** Deal's company */
  company: CompanyFindInterface;
  /** Deal's direction */
  direction: DirectionFindInterface;
  /** Deal's unit */
  unit: UnitFindInterface;
  /** Deal's filial */
  filial: FilialFindInterface;
  /** Group this deal belongs to (optional) */
  group?: DealsGroupFindInterface;
  /** User auditors (deals group participants) */
  userAuditors: UserFindInterface[];
  /** Group auditors (deals group participants) */
  groupAuditors: UserGroupFindInterface[];
  /** Deal's 1C ERP databases synchronization history */
  synchronizeHistory?: DealSynchronizeHistoryInterface[];
  /** Documents statistics for this deal */
  documentStatistics: DocumentsStatisticsFindInterface;
  /** Reminder for current authorized user (if any) */
  reminder?: ReminderFindInterface;
  thread?: ThreadFindInterface;
  /** Integration deal ID */
  externalSyncId: string;
  orders?: DealOrderFindInterface[];
  /** Projects to associate with deal */
  projects?: ProjectFindInterface[];
  /** HelpDesk tasks to associate with deal */
  helpdeskTasks?: HelpdeskTaskFindInterface[];
}

export const DealFindDto = SerdeDtoClass.create<DealFindInterface>(BusinessDtoKeysEnum.DEAL_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Обновлён',
    serializer: yup.date(),
  },
  shippingDate: {
    label: 'Дата отгрузки',
    serializer: yup.date(),
  },
  deleted: {
    label: 'Дата удаления',
    serializer: yup.date(),
  },
  number: {
    sortable: true,
    label: 'Номер сделки',
    serializer: yup.string(),
  },
  name: {
    sortable: true,
    label: 'Имя сделки',
    serializer: yup.string(),
  },
  amount: {
    sortable: true,
    label: 'Стоимость',
    serializer: yup.number(),
  },
  comment: {
    optional: true,
    label: 'Комментарий',
    serializer: yup.string(),
  },
  saleDiff: {
    optional: true,
    label: 'Разница доходов и расходов',
    serializer: yup.number(),
  },
  filesCount: {
    optional: true,
    label: 'Количество файлов',
    serializer: yup.number(),
  },
  priority: {
    optional: true,
    sortable: true,
    label: 'Приоритет',
    serializer: yup
      .string()
      .oneOf(Object.values(HelpdeskTaskPriorityEnum)),
  },
  plannedMin: {
    optional: true,
    label: 'Минимальное планируемое время',
    serializer: yup.number(),
  },
  factMin: {
    label: 'Фактическое время',
    serializer: yup.number(),
  },
  amountCharges: {
    optional: true,
    label: 'Сумма сборов',
    serializer: yup.number(),
  },
  amountPayment: {
    optional: true,
    label: 'Сумма оплаты',
    serializer: yup.number(),
  },
  createdBy: {
    optional: true,
    label: 'Создатель',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  responsible: {
    optional: true,
    label: 'Ответственный',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  status: {
    sortable: true,
    label: 'Статус',
    nested: DealStatusFindDto,
    serializer: (_, nested) => nested,
  },
  contragent: {
    optional: true,
    sortable: true,
    label: 'Контрагент',
    nested: ContragentFindDto,
    serializer: (_, nested) => nested,
  },
  company: {
    label: 'Компания',
    nested: CompanyFindDto,
    serializer: (_, nested) => nested,
  },
  direction: {
    label: 'БС',
    nested: DirectionFindDto,
    serializer: (_, nested) => nested,
  },
  filial: {
    label: 'Филиал',
    nested: FilialFindDto,
    serializer: (_, nested) => nested,
  },
  unit: {
    label: 'БЕ',
    nested: UnitFindDto,
    serializer: (_, nested) => nested,
  },
  group: {
    optional: true,
    sortable: true,
    label: 'Группа',
    nested: DealsGroupFindDto,
    serializer: (_, nested) => nested,
  },
  userAuditors: {
    label: 'Аудиторы',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  groupAuditors: {
    label: 'Группы аудиторов',
    nested: UserGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  synchronizeHistory: {
    optional: true,
    label: 'История синхронизаций',
    nested: DealSynchronizeHistoryDto,
    serializer: (_, nested) => yup.array(nested),
  },
  documentStatistics: {
    sortable: true,
    label: 'Статистика документов',
    nested: DocumentsStatisticsFindDto,
    serializer: (_, nested) => nested,
  },
  reminder: {
    optional: true,
    label: 'Напоминание',
    nested: ReminderFindDto,
    serializer: (_, nested) => nested,
  },
  thread: {
    optional: true,
    label: 'thread',
    nested: MessengerDtoKeysEnum.THREAD_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  externalSyncId: {
    label: 'Внешний номер синхронизации',
    serializer: yup.string(),
  },
  orders: {
    optional: true,
    label: 'Заказы',
    nested: DealOrderFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  projects: {
    optional: true,
    label: 'Проекты',
    nested: ProjectDtoKeysEnum.PROJECT_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  helpdeskTasks: {
    optional: true,
    label: 'Заявки HD',
    nested: HelpdeskDtoKeyEnum.HELPDESK_TASK_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
});
